import { useRef } from 'react';

import { useDepartmentDetails } from '~/api/query/departmentDetails';
import { USER_ROLES } from '~/features/auth/roles';
import { useAdminUserCreateForm } from '~/features/hcpAdministration/users/useAdminUserCreateForm';
import { useModal } from '~/features/modals/useModal';
import { buildTestId } from '~/utils/buildTestId';

import { ContentLoader } from '../ContentLoader';
import {
  AdminUserForm,
  type AdminUserFormRef,
} from '../forms/adminUser/AdminUserForm';
import { Modal } from '../Modal';

type AdminUserCreateModalProps = {
  departmentId?: string;
  testID: string;
};

export const AdminUserCreateModal = ({
  departmentId,
  testID,
}: AdminUserCreateModalProps) => {
  const adminFormRef = useRef<AdminUserFormRef>(null);

  const { closeModal } = useModal();

  // Fetch department details if departmentId is provided
  const {
    data: departmentDetails,
    isLoading: isDeptDetailsLoading,
    isError: deptDetailsError,
  } = useDepartmentDetails({
    departmentId,
  });

  const {
    handleSubmitClick,
    handleCreateUser,
    isFormLoading,
    isNextStepDisabled,
  } = useAdminUserCreateForm({ formRef: adminFormRef });

  const displayDepartmentData = departmentId != null;
  const isLoading = isDeptDetailsLoading || isFormLoading;

  return (
    <Modal
      title="Add User"
      testID={testID}
      hasCloseButton={false}
      size="m"
      primaryAction={{
        label: 'Complete',
        onPress: () => void handleSubmitClick(),
        disabled: isNextStepDisabled,
      }}
      secondaryAction={{
        label: 'Cancel',
        onPress: () => {
          closeModal('AdminUserCreate');
        },
      }}
      onOpenChange={() => {
        closeModal('AdminUserCreate');
      }}
    >
      {displayDepartmentData && isLoading && (
        <ContentLoader testID={buildTestId({ testID, suffix: 'loader' })} />
      )}

      {/* Optionally handle department details error */}
      {deptDetailsError && <div>Failed to load department details</div>}

      {/* Only show form if department details loaded (if required) */}
      {(!displayDepartmentData || departmentDetails) && (
        <AdminUserForm
          ref={adminFormRef}
          onSubmit={handleCreateUser}
          defaultValues={
            departmentDetails
              ? {
                  departmentId: departmentDetails.departmentId,
                  role: USER_ROLES.HcpUser,
                }
              : undefined
          }
          testID={buildTestId({ testID, suffix: 'form' })}
        />
      )}
    </Modal>
  );
};
